/**
 * Sheet „Gebäude anlegen" (Spec 02a, „Oberfläche · Sheet").
 *
 * Sichtbar ist nur die Bezeichnung; alles andere steckt hinter „Weitere
 * Angaben". Das Elternobjekt kommt aus dem Kontext (`parentId`/`parentLabel`)
 * und steht nur als Text im Kopf — kein Formular, in dem man erst auswählt,
 * wohin das Gebäude gehört (`ARCHITECTURE.md` 6.4). Für „aus dem Kontext
 * anlegen" ist das der geöffnete Datensatz selbst; für `building-duplicate`
 * (Spec 02b) ist es dessen eigenes Elternobjekt — derselbe Prop, zwei
 * Aufrufer in `routes/ObjectDetail.tsx`.
 *
 * „Anlegen und noch eins" speichert seit Spec 02b optimistisch
 * (`useCreateBuilding`s `optimistic`-Flag) — der primäre Knopf bleibt
 * synchron.
 */

import { useNavigate } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { areaToInputValue, parseAreaInput } from '@/features/properties/area'
import { useCreateBuilding } from '@/features/properties/api'
import type { BuildingCreate, BuildingDetail } from '@/features/properties/api'
import { FIELD_CLASS, FormField, FormTextareaField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { integerToInputValue, parseIntegerInput } from '@/features/properties/integer'

interface BuildingSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly parentId: string
  readonly parentLabel: string
  /** Gesetzt für `building-duplicate` (Spec 02b, „Schnellerfassung ·
   * Duplizieren"): belegt alle Felder außer der Bezeichnung vor, die bleibt
   * leer und fokussiert. */
  readonly duplicateFrom?: BuildingDetail
}

const EMPTY_FORM = {
  label: '',
  builtYear: '',
  heatingType: '',
  totalAreaSqm: '',
  notes: '',
}

type FormValues = typeof EMPTY_FORM

function deriveDuplicateForm(source: BuildingDetail): FormValues {
  return {
    label: '',
    builtYear: source.built_year === null ? '' : integerToInputValue(source.built_year),
    heatingType: source.heating_type ?? '',
    totalAreaSqm: source.total_area_sqm === null ? '' : areaToInputValue(source.total_area_sqm),
    notes: source.notes ?? '',
  }
}

function toBody(parentId: string, form: FormValues): BuildingCreate {
  return {
    parent_id: parentId,
    label: form.label,
    built_year: parseIntegerInput(form.builtYear),
    heating_type: form.heatingType.trim() === '' ? null : form.heatingType.trim(),
    total_area_sqm: parseAreaInput(form.totalAreaSqm),
    notes: form.notes.trim() === '' ? null : form.notes.trim(),
  }
}

export function BuildingSheet({ open, onOpenChange, parentId, parentLabel, duplicateFrom }: BuildingSheetProps) {
  const navigate = useNavigate()
  const createMutation = useCreateBuilding()
  const labelRef = useRef<HTMLInputElement>(null)

  // Lazy Initialisierer statt `EMPTY_FORM`: Seit die Detailquery (ADR 0028)
  // erst mit dem Öffnen des Duplizieren-Sheets scharf geschaltet wird,
  // mountet es für „Duplizieren" erstmals bereits mit `open === true` — die
  // untere „Jedes Öffnen beginnt frisch"-Umschaltung sieht dann nie einen
  // Wechsel von `false` auf `true` und säht das Formular nie.
  const [form, setForm] = useState<FormValues>(() =>
    open && duplicateFrom !== undefined ? deriveDuplicateForm(duplicateFrom) : EMPTY_FORM,
  )
  const [isMoreOpen, setIsMoreOpen] = useState(() => open && duplicateFrom !== undefined)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch (React-Muster „Adjusting state when a prop
  // changes", während des Renderns statt in einem Effekt — sonst wäre das
  // alte Formular für einen Durchlauf sichtbar). Beim Duplizieren vorbelegt,
  // „Weitere Angaben" gleich aufgeklappt — sonst sähen die vorbelegten Felder
  // aus wie ein leeres Formular.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(duplicateFrom !== undefined ? deriveDuplicateForm(duplicateFrom) : EMPTY_FORM)
      setIsMoreOpen(duplicateFrom !== undefined)
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  function updateField(name: keyof FormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function resetForAnother() {
    setForm(EMPTY_FORM)
    setIsMoreOpen(false)
    setFieldError(undefined)
    setGeneralError(undefined)
    labelRef.current?.focus()
  }

  async function submit(andAnother: boolean) {
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      const created = await createMutation.mutateAsync({ body: toBody(parentId, form), optimistic: andAnother })
      if (andAnother) {
        resetForAnother()
      } else {
        onOpenChange(false)
        await navigate({ to: '/objekt/$entityId', params: { entityId: created.id }, search: { tab: undefined } })
      }
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          labelRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form onSubmit={(event) => void handleSubmit(event)} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">Gebäude anlegen</SheetTitle>
            <p data-testid="building-create-parent" className="text-label text-muted">
              {parentLabel}
            </p>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <FormField
              ref={labelRef}
              label="Bezeichnung"
              testId="building-create-label"
              apiField="label"
              errorPrefix="building-create"
              required
              value={form.label}
              onChange={updateField('label')}
              error={fieldError}
            />

            <details
              open={isMoreOpen}
              onToggle={(event) => {
                setIsMoreOpen(event.currentTarget.open)
              }}
            >
              <summary
                data-testid="building-create-more"
                className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2"
              >
                Weitere Angaben
              </summary>

              <div className="flex flex-col gap-4 pt-2">
                <FormField
                  label="Baujahr"
                  testId="building-create-built-year"
                  apiField="built_year"
                  errorPrefix="building-create"
                  inputMode="numeric"
                  value={form.builtYear}
                  onChange={updateField('builtYear')}
                  error={fieldError}
                />
                <FormField
                  label="Heizungsart"
                  testId="building-create-heating-type"
                  apiField="heating_type"
                  errorPrefix="building-create"
                  value={form.heatingType}
                  onChange={updateField('heatingType')}
                  error={fieldError}
                />
                <FormField
                  label="Gesamtfläche (m²)"
                  testId="building-create-total-area-sqm"
                  apiField="total_area_sqm"
                  errorPrefix="building-create"
                  inputMode="decimal"
                  value={form.totalAreaSqm}
                  onChange={updateField('totalAreaSqm')}
                  error={fieldError}
                />
                <FormTextareaField
                  label="Notizen"
                  testId="building-create-notes"
                  apiField="notes"
                  errorPrefix="building-create"
                  value={form.notes}
                  onChange={updateField('notes')}
                  error={fieldError}
                />
              </div>
            </details>
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="building-create-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="building-create-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="button"
                data-testid="building-create-save-another"
                disabled={createMutation.isPending}
                aria-busy={createMutation.isPending}
                onClick={() => void submit(true)}
                className={`text-text px-4 ${FIELD_CLASS} disabled:opacity-60`}
              >
                Anlegen und noch eins
              </button>
              <button
                type="submit"
                data-testid="building-create-save"
                disabled={createMutation.isPending}
                aria-busy={createMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Gebäude anlegen
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
